/**
 * Showcase Filters - Custom Element
 * Handles filter tabs, panels, range sliders, and chip toggles.
 */
class ShowcaseFilters extends HTMLElement {
  constructor() {
    super();
    this.activeFilters = {
      tags: new Set(),
      minPrice: 0,
      maxPrice: Infinity,
      minHP: 0,
      maxHP: Infinity
    };
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.tabs = this.querySelectorAll('[data-filter-tab]');
    this.panels = this.querySelectorAll('[data-filter-panel]');
    this.chips = this.querySelectorAll('[data-filter-chip]');
    this.grid = document.querySelector('.showcase-grid');

    this.bindTabs();
    this.bindChips();
    this.bindRangeSliders();
    this.bindSticky();

    // Mark initialization complete so applyFilters knows
    // not to trigger loadAllProducts during startup
    this._filtersReady = true;
  }

  bindTabs() {
    this.tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const panelId = tab.dataset.filterTab;
        const wasActive = tab.classList.contains('is-active');

        // Deactivate all tabs
        this.tabs.forEach(t => t.classList.remove('is-active'));

        // Fade out current panel, then show new one
        const currentPanel = this.querySelector('.showcase-filters__panel.is-active');

        if (wasActive) {
          // Closing the same tab
          if (currentPanel) currentPanel.classList.remove('is-active');
        } else {
          // Switching to a new tab
          tab.classList.add('is-active');
          const nextPanel = this.querySelector(`[data-filter-panel="${panelId}"]`);

          if (currentPanel && currentPanel !== nextPanel) {
            // Fade out old, then fade in new
            currentPanel.classList.remove('is-active');
            setTimeout(() => {
              if (nextPanel) nextPanel.classList.add('is-active');
            }, 80);
          } else if (nextPanel) {
            nextPanel.classList.add('is-active');
          }
        }
      });
    });
  }

  bindChips() {
    this.chips.forEach(chip => {
      chip.addEventListener('click', () => {
        chip.classList.toggle('is-active');
        const tag = chip.dataset.filterChip;

        if (chip.classList.contains('is-active')) {
          this.activeFilters.tags.add(tag);
        } else {
          this.activeFilters.tags.delete(tag);
        }

        this.applyFilters();
      });
    });
  }

  bindRangeSliders() {
    const sliderGroups = this.querySelectorAll('[data-range-slider]');
    sliderGroups.forEach(group => {
      const minInput = group.querySelector('[data-range-min]');
      const maxInput = group.querySelector('[data-range-max]');
      const fill = group.querySelector('.showcase-filter__range-fill');
      const valuesEl = group.closest('.showcase-filters__panel')
        ?.querySelector('[data-range-display]');

      if (!minInput || !maxInput) return;

      const sliderType = group.dataset.rangeSlider;

      const updateSlider = () => {
        const min = parseInt(minInput.min);
        const max = parseInt(minInput.max);
        const minVal = parseInt(minInput.value);
        const maxVal = parseInt(maxInput.value);

        // Prevent overlap
        if (minVal > maxVal - 10) {
          minInput.value = maxVal - 10;
        }

        const leftPercent = ((parseInt(minInput.value) - min) / (max - min)) * 100;
        const rightPercent = 100 - ((parseInt(maxInput.value) - min) / (max - min)) * 100;

        if (fill) {
          fill.style.left = leftPercent + '%';
          fill.style.right = rightPercent + '%';
        }

        if (valuesEl) {
          const prefix = sliderType === 'hp' ? '' : '$';
          const suffix = sliderType === 'hp' ? 'HP' : '';
          valuesEl.textContent = `${prefix}${parseInt(minInput.value).toLocaleString()}${suffix} - ${prefix}${parseInt(maxInput.value).toLocaleString()}${suffix}`;
        }

        // Store filter values and apply live
        if (sliderType === 'price') {
          this.activeFilters.minPrice = parseInt(minInput.value);
          this.activeFilters.maxPrice = parseInt(maxInput.value);
        } else if (sliderType === 'hp') {
          this.activeFilters.minHP = parseInt(minInput.value);
          this.activeFilters.maxHP = parseInt(maxInput.value);
        }
        this.applyFilters();
      };

      minInput.addEventListener('input', updateSlider);
      maxInput.addEventListener('input', updateSlider);
      // Initialize visual state without filtering (filters start wide open)
      updateSlider();
    });
  }

  bindSticky() {
    this.placeholder = document.createElement('div');
    this.placeholder.className = 'showcase-filters-placeholder';
    this.parentNode.insertBefore(this.placeholder, this);

    this.isStuck = false;

    const getHeaderHeight = () => {
      const header = document.querySelector('#header-component[data-sticky-state="active"]');
      return header ? header.offsetHeight : 0;
    };

    // Pre-cache placeholder height
    this.placeholder.style.height = this.offsetHeight + 'px';

    let ticking = false;

    const update = () => {
      const headerH = getHeaderHeight();
      // Use the real-time bounding rect — when stuck, check the placeholder
      const rect = this.isStuck
        ? this.placeholder.getBoundingClientRect()
        : this.getBoundingClientRect();

      if (!this.isStuck && rect.top <= headerH) {
        // Capture exact size and position before going fixed
        const w = this.offsetWidth;
        const l = rect.left;
        this.placeholder.style.height = this.offsetHeight + 'px';
        this.placeholder.classList.add('is-visible');
        this.style.width = w + 'px';
        this.style.left = l + 'px';
        this.style.right = 'auto';
        this.style.top = headerH + 'px';
        this.isStuck = true;
        this.classList.add('is-stuck');
      } else if (this.isStuck && rect.top > headerH) {
        this.isStuck = false;
        this.classList.remove('is-stuck');
        this.style.top = '';
        this.style.width = '';
        this.style.left = '';
        this.style.right = '';
        this.placeholder.classList.remove('is-visible');
      } else if (this.isStuck) {
        this.style.top = headerH + 'px';
      }

      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    update();
  }

  /**
   * Tests whether a single card matches a given set of filters.
   * Reused by both applyFilters() and countMatchesIf() to stay DRY.
   */
  cardMatchesFilters(card, filters) {
    const productCard = card.querySelector('.showcase-card');
    if (!productCard) return false;

    const productTags = (productCard.dataset.productTags || '').toLowerCase().split(',').map(t => t.trim());

    // Tag filter
    if (filters.tags.size > 0) {
      const matchesTag = [...filters.tags].some(tag =>
        productTags.includes(tag.toLowerCase())
      );
      if (!matchesTag) return false;
    }

    // Price filter
    if (filters.minPrice > 0 || filters.maxPrice < Infinity) {
      const priceCents = parseInt(productCard.dataset.productPrice || '0');
      const priceDollars = priceCents / 100;
      if (priceDollars < filters.minPrice || priceDollars > filters.maxPrice) {
        return false;
      }
    }

    // HP filter — tags use hp_XX format (e.g. hp_24, hp_58)
    if (filters.minHP > 0 || filters.maxHP < Infinity) {
      const hpTag = productTags.find(t => /^hp_\d+$/.test(t));
      if (hpTag) {
        const hp = parseInt(hpTag.replace('hp_', ''));
        if (!isNaN(hp) && (hp < filters.minHP || hp > filters.maxHP)) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Counts how many cards would match if the filters were modified.
   */
  countMatchesIf(cards, modifiedFilters) {
    let count = 0;
    cards.forEach(card => {
      if (this.cardMatchesFilters(card, modifiedFilters)) count++;
    });
    return count;
  }

  applyFilters() {
    if (!this.grid) return;

    // During initialization, sliders fire input events that store their
    // starting values. We don't want those to trigger loading all products.
    if (!this._filtersReady) return;

    // If not all products are loaded yet, load them first so
    // filtering runs against the full catalog
    if (this.grid.loadAllProducts) {
      const loadAll = this.grid.loadAllProducts;
      // Remove reference so we only trigger this once
      delete this.grid.loadAllProducts;

      // Hide sentinel immediately so it doesn't show alongside filter results
      const sentinel = this.grid.querySelector('[data-sentinel]');
      if (sentinel) sentinel.style.display = 'none';

      this.grid.classList.add('is-filtering');
      this._loadingAll = loadAll().then(() => {
        this._loadingAll = null;
        this._runFilter();
      });
      return;
    }

    // If products are still being loaded, wait for that to finish
    // before running the filter (slider drags fire rapidly)
    if (this._loadingAll) {
      this._loadingAll.then(() => {
        this._runFilter();
      });
      return;
    }

    // Fade grid out to signal a refresh
    this.grid.classList.add('is-filtering');

    // Debounce: cancel any pending filter so rapid slider drags
    // only apply the final position
    clearTimeout(this._filterTimeout);

    this._filterTimeout = setTimeout(() => {
      this._runFilter();
    }, 250);
  }

  /**
   * Internal method that actually applies the filter logic to the grid.
   * Separated from applyFilters() so it can be called after loadAllProducts resolves.
   */
  _runFilter() {
    // Hide sentinel if still present (safety net)
    const sentinel = this.grid.querySelector('[data-sentinel]');
    if (sentinel) sentinel.style.display = 'none';

    // Select only real product cards, not the sentinel or empty state
    const cards = this.grid.querySelectorAll('li:not([data-sentinel]):not([data-empty-state])');
    let visibleCount = 0;

    cards.forEach(card => {
      const show = this.cardMatchesFilters(card, this.activeFilters);
      card.style.display = show ? '' : 'none';
      if (show) visibleCount++;
    });

    if (visibleCount === 0) {
      this.showEmptyState(cards);
    } else {
      this.hideEmptyState();
    }

    // Fade grid back in
    this.grid.classList.remove('is-filtering');
  }

  /**
   * Builds the list of all currently active adjustments that could be removed.
   * Each entry has: label (display name), apply (function to execute the removal),
   * and a modify function that returns a modified copy of filters without this adjustment.
   */
  buildAdjustments() {
    const adjustments = [];

    // One entry per active tag
    for (const tag of this.activeFilters.tags) {
      const chipEl = this.querySelector(`[data-filter-chip="${tag}"]`);
      const label = chipEl ? chipEl.textContent.trim() : tag;
      adjustments.push({
        label: `Remove "${label}"`,
        modify: (filters) => ({
          ...filters,
          tags: new Set([...filters.tags].filter(t => t !== tag))
        }),
        apply: () => {
          this.activeFilters.tags.delete(tag);
          if (chipEl) chipEl.classList.remove('is-active');
        }
      });
    }

    // Price range
    if (this.activeFilters.minPrice > 0 || this.activeFilters.maxPrice < Infinity) {
      adjustments.push({
        label: 'Reset price range',
        modify: (filters) => ({ ...filters, minPrice: 0, maxPrice: Infinity }),
        apply: () => {
          this.activeFilters.minPrice = 0;
          this.activeFilters.maxPrice = Infinity;
          this.resetSlider('price');
        }
      });
    }

    // HP range
    if (this.activeFilters.minHP > 0 || this.activeFilters.maxHP < Infinity) {
      adjustments.push({
        label: 'Reset HP range',
        modify: (filters) => ({ ...filters, minHP: 0, maxHP: Infinity }),
        apply: () => {
          this.activeFilters.minHP = 0;
          this.activeFilters.maxHP = Infinity;
          this.resetSlider('hp');
        }
      });
    }

    return adjustments;
  }

  /**
   * Resets a range slider to its full min/max range and updates the visual fill.
   */
  resetSlider(type) {
    const slider = this.querySelector(`[data-range-slider="${type}"]`);
    if (!slider) return;
    const minInput = slider.querySelector('[data-range-min]');
    const maxInput = slider.querySelector('[data-range-max]');
    if (minInput) { minInput.value = minInput.min; minInput.dispatchEvent(new Event('input')); }
    if (maxInput) { maxInput.value = maxInput.max; maxInput.dispatchEvent(new Event('input')); }
  }

  /**
   * Creates a copy of activeFilters that can be spread/modified.
   */
  cloneFilters() {
    return {
      tags: new Set(this.activeFilters.tags),
      minPrice: this.activeFilters.minPrice,
      maxPrice: this.activeFilters.maxPrice,
      minHP: this.activeFilters.minHP,
      maxHP: this.activeFilters.maxHP
    };
  }

  showEmptyState(cards) {
    this.hideEmptyState();

    const container = document.createElement('li');
    container.className = 'showcase-empty-state';
    container.setAttribute('data-empty-state', '');

    container.innerHTML = `
      <svg class="showcase-empty-state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="11" cy="11" r="8"></circle>
        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        <line x1="8" y1="11" x2="14" y2="11"></line>
      </svg>
      <div class="showcase-empty-state__message">No products match your current filters</div>
      <div class="showcase-empty-state__hint">Try adjusting your selections to see more options</div>
      <div class="showcase-empty-state__pills"></div>
    `;

    const pillsContainer = container.querySelector('.showcase-empty-state__pills');
    const adjustments = this.buildAdjustments();

    // --- Pass 1: single-filter removal pills ---
    const singlePills = [];
    adjustments.forEach((adj, i) => {
      const modified = adj.modify(this.cloneFilters());
      const count = this.countMatchesIf(cards, modified);
      if (count > 0) {
        singlePills.push({ adj, count, index: i });
      }
    });

    for (const { adj, count } of singlePills) {
      const pill = document.createElement('button');
      pill.className = 'showcase-empty-state__pill';
      pill.innerHTML = `${adj.label} <span class="showcase-empty-state__pill-count">+${count}</span>`;
      pill.addEventListener('click', () => { adj.apply(); this.applyFilters(); });
      pillsContainer.appendChild(pill);
    }

    // --- Pass 2: if no single removals help, try pairs ---
    if (singlePills.length === 0 && adjustments.length >= 2) {
      for (let i = 0; i < adjustments.length; i++) {
        for (let j = i + 1; j < adjustments.length; j++) {
          const a = adjustments[i];
          const b = adjustments[j];
          // Apply both modifications
          const modified = b.modify(a.modify(this.cloneFilters()));
          const count = this.countMatchesIf(cards, modified);
          if (count > 0) {
            const pill = document.createElement('button');
            pill.className = 'showcase-empty-state__pill';
            pill.innerHTML = `${a.label} + ${b.label} <span class="showcase-empty-state__pill-count">+${count}</span>`;
            pill.addEventListener('click', () => { a.apply(); b.apply(); this.applyFilters(); });
            pillsContainer.appendChild(pill);
          }
        }
      }
    }

    // "Clear all filters" button
    const totalProducts = cards.length;
    const clearBtn = document.createElement('button');
    clearBtn.className = 'showcase-empty-state__clear';
    clearBtn.textContent = `Clear all filters (${totalProducts} products)`;
    clearBtn.addEventListener('click', () => {
      this.activeFilters.tags.clear();
      this.activeFilters.minPrice = 0;
      this.activeFilters.maxPrice = Infinity;
      this.activeFilters.minHP = 0;
      this.activeFilters.maxHP = Infinity;
      this.chips.forEach(chip => chip.classList.remove('is-active'));
      const sliders = this.querySelectorAll('[data-range-slider]');
      sliders.forEach(group => {
        const minInput = group.querySelector('[data-range-min]');
        const maxInput = group.querySelector('[data-range-max]');
        if (minInput) { minInput.value = minInput.min; minInput.dispatchEvent(new Event('input')); }
        if (maxInput) { maxInput.value = maxInput.max; maxInput.dispatchEvent(new Event('input')); }
      });
      this.applyFilters();
    });
    container.appendChild(clearBtn);

    this.grid.appendChild(container);
  }

  hideEmptyState() {
    if (!this.grid) return;
    const existing = this.grid.querySelector('[data-empty-state]');
    if (existing) existing.remove();
  }
}

customElements.define('showcase-filters', ShowcaseFilters);
